import { Block } from "@components/block";
import { DealModal } from "@components/deal-modal";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, DoubleText, Table } from "@components/table";
import { formatDate, formatTime } from "@helpers/date";
import { useBoolState } from "@hooks/use-bool-state";
import { usePagination } from "@hooks/use-pagination";
import { useEffect, useState } from "react";
import {
  MerchantOrder,
  MerchantOrderForm,
  MerchantOrderStatus,
  MerchantOrderType,
} from "../api";
import { useMerchantOrders } from "../index";
import { FilterConfig } from "@components/filter-modal";
import { SelectOption } from "@components/select";
import { useCashRegisters } from "@features/cash-registers";
import { useUrlFilters } from "@hooks/useUrlFilters";
import { store } from "@app/store";
import { setLastAppealId } from "@features/appeals/slice";
import styles from "./styles.module.scss";
import { Button } from "@components/button";
import Arrow from "@assets/icons/arrow-right.svg";
import { formatRubles, formatUsdt } from "@helpers/format";
import { OrderModal } from "@components/deal-modal/orderModal";
import { EllipsisText } from "@components/ellipsis-text";
import { OrderCSVModal } from "@components/order-csv/OrderCSV";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";

const config: Config<MerchantOrder> = [
  {
    source: "status",
    label: "Статус",
    renderComponent: (status: MerchantOrderStatus) => {
      let text = "";
      let type = StatusBlockType.Success;

      switch (status) {
        case MerchantOrderStatus.Canceled:
          text = "Отменен";
          type = StatusBlockType.Error;
          break;
        case MerchantOrderStatus.Completed:
          text = "Зачислен";
          type = StatusBlockType.Success;
          break;
        case MerchantOrderStatus.Pending:
        case MerchantOrderStatus.Processing:
          text = "В процессе";
          type = StatusBlockType.Warning;
          break;
        case MerchantOrderStatus.DisputeOpen:
          text = "Спор открыт";
          type = StatusBlockType.Warning;
          break;
      }

      return (
        <StatusBlock type={type} text={text} style={{ textWrap: "nowrap" }} />
      );
    },
  },
  {
    source: "cache",
    label: "Касса",
  },
  {
    source: "created_at",
    label: "Дата",
    renderComponent: (cellData: string) => (
      <DoubleText
        text={formatTime(new Date(cellData))}
        subText={formatDate(new Date(cellData))}
      />
    ),
  },
  {
    source: "invid",
    label: "Номер заявки",
    renderComponent: (data) => <EllipsisText text={data} copy />,
  },
  {
    label: "Сумма заявки",
    source: "order_start_amount",
    renderComponent: (cellData, item) => {
      const usdt = formatUsdt(item.order_start_amount_usdt);
      const rubles = formatRubles(cellData);
      return <DoubleText text={rubles} subText={usdt} />;
    },
  },
  {
    label: "Измененная сумма",
    source: "merchant_amount",
    renderComponent: (data, item) => {
      if (item.merchant_amount_usdt == null || data == null) return "";
      if (item.merchant_amount_usdt == 0 || data == 0) return "";
      const usdt = formatUsdt(item.merchant_amount_usdt);
      const rubles = formatRubles(data);

      return <DoubleText subText={usdt} text={rubles} />;
    },
  },
  {
    label: "Итоговая сумма",
    source: "order_final_amount",
    renderComponent: (cellData, item) => {
      const usdt = formatUsdt(item.order_final_amount_usdt);
      const rubles = formatRubles(cellData);
      return <DoubleText text={rubles} subText={usdt} />;
    },
  },
  {
    source: "ern_usdt",
    label: "Доход / Списание",
    renderComponent: (cellData: number, item) => (
      <span
        style={{
          color: item.type == MerchantOrderType.PayOut ? "red" : "green",
        }}
      >
        {formatUsdt(cellData)}
      </span>
    ),
  },
  {
    source: "method",
    label: "Метод",
  },
  // {
  //   source: "type",
  //   label: "Тип",
  //   renderComponent: (cellData: MerchantOrderType) =>
  //     cellData === MerchantOrderType.PayIn ? "Pay-in" : "Pay-out",
  // },
  {
    source: "appeal",
    label: "",
    renderComponent: () => (
      <a style={{ color: "#1F8D7D", textDecoration: "underline" }}>
        Посмотреть
      </a>
    ),
  },
];

export const Transactions = () => {
  const [isModalOpen, showModal, hideModal] = useBoolState(false);
  const [isAppealModalOpen, showAppealModal, hideAppealModal] =
    useBoolState(false);
  const [isCSVModalOpen, showCSVModal, hideCSVModal] = useBoolState(false);

  const { state, getMerchantOrders } = useMerchantOrders();
  const { page, limit, setPage, setLimit } = usePagination(100);
  const { setParams } = useUrlFilters();
  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.Order) {
      getMerchantOrders({ page, limit });
    }
  }, [lastMessage, page, limit]);

  useEffect(() => {
    const data: Record<string, unknown> = {};
    const url = new URL(window.location.href);
    const { searchParams } = url;
    for (const [key, value] of searchParams.entries()) {
      data[key] = value;
    }
    data.page = page;
    data.limit = limit;
    getMerchantOrders(data as MerchantOrderForm);
  }, [getMerchantOrders, limit, page]);

  // Fetch cash registers
  const [cashOptions, setCashOptions] = useState<SelectOption[]>([]);
  const { state: cacheState, getCashRegisters } = useCashRegisters();

  useEffect(() => {
    getCashRegisters();
  }, [getCashRegisters]);

  useEffect(() => {
    if (cacheState.caches) {
      setCashOptions(
        cacheState.caches.map((cash) => ({ text: cash.name, value: cash.id }))
      );
    }
  }, [cacheState]);

  const onSubmit = (data: MerchantOrderForm) => {
    setParams(data);
    getMerchantOrders(data);
  };
  const filterConfig: FilterConfig<MerchantOrderForm> = [
    {
      type: "select",
      options: cashOptions,
      name: "cache",
      label: "Кассы",
    },
    {
      type: "sum",
      minName: "min_sum",
      maxName: "max_sum",
    },
    {
      type: "select",
      name: "status",
      label: "Статус заявки",
      options: [
        { text: "Зачислен", value: "completed" },
        { text: "Отменен", value: "canceled" },
        { text: "Спор открыт", value: "dispute_open" },
        { text: "В процессе", value: "pending" },
      ],
    },
    // {
    //   type: "select",
    //   name: "type",
    //   label: "Тип заявки",
    //   options: [
    //     { text: "Pay-in", value: "pay-in" },
    //     { text: "Pay-out", value: "pay-out" },
    //   ],
    // },
    {
      type: "date",
      nameStart: "date_start",
      nameEnd: "date_end",
      returnValue: "number",
    },
  ];
  // useEffect(() => {
  // getMerchantOrders();
  // }, [getMerchantOrders]);

  const [currentDeal, setCurrentDeal] = useState<MerchantOrder>();

  const onDealClick = (item: MerchantOrder) => {
    store.dispatch(setLastAppealId(item.appeal ?? null));
    showModal();
    setCurrentDeal(item);
  };
  const onSearch = (query: string) => {
    setParams({invid: query});
    getMerchantOrders({invid: query, limit: 100, page: 1});
  }

  return (
    <>
      <Block className={styles.deals}>
        <div className={styles.header}>
          <h3>История сделок</h3>
          <Button icon={Arrow} onClick={showCSVModal}>
            Создать выгрузку
          </Button>
        </div>
        <Table
          placeholder="Поиск по сделкам"
          withFilter
          className={styles.table}
          filterProps={{
            filterConfig: filterConfig,
            // validationRules: validationRules,
            onSubmit: onSubmit,
          }}
          config={config}
          data={state.orders}
          loading={state.loading}
          onRowClick={onDealClick}
          setPage={setPage}
          setLimit={setLimit}
          limits={[100, 200, 300]}
          page={page}
          limit={limit}
          totalCount={state.count}
          onSearch={onSearch}
        />
      </Block>
      {currentDeal && (
        <OrderModal
          isModalOpen={isModalOpen}
          hideModal={hideModal}
          data={currentDeal}
          setAppealStatus={showAppealModal}
        />
      )}
      {currentDeal && (
        <DealModal
          isModalOpen={isAppealModalOpen}
          hideModal={hideAppealModal}
          data={currentDeal}
        />
      )}
      {isCSVModalOpen && (
        <OrderCSVModal isModalOpen={isCSVModalOpen} hideModal={hideCSVModal} />
      )}
    </>
  );
};

// function getFromUrlParams(): MerchantOrderForm {
//   const form: MerchantOrderForm = { limit: 100, page: 1 };
//   const limit = getFilterParamFromQuery<MerchantOrderForm>("limit");
//   if (limit && !isNaN(+limit)) form.limit = +limit;
//   const page = getFilterParamFromQuery<MerchantOrderForm>("page");
//   if (page && !isNaN(+page)) form.page = +page;
//   const min_sum = getFilterParamFromQuery<MerchantOrderForm>("min_sum");
//   if (min_sum && !isNaN(+min_sum)) form.min_sum = +min_sum;
//   const max_sum = getFilterParamFromQuery<MerchantOrderForm>("max_sum");
//   if (max_sum && !isNaN(+max_sum)) form.max_sum = +max_sum;
//   const cache = getFilterParamFromQuery<MerchantOrderForm>("cache");
//   if (typeof cache === "string") form.cache = cache;
//   const status = getFilterParamFromQuery<MerchantOrderForm>("status");
//   if (typeof status === "string") form.status = status;
//   const date_start = getFilterParamFromQuery<MerchantOrderForm>("date_start");
//   if (date_start && !isNaN(+date_start)) form.date_start = +date_start;
//   const date_end = getFilterParamFromQuery<MerchantOrderForm>("date_end");
//   if (date_end && !isNaN(+date_end)) form.date_end = +date_end;
//   return form;
// }
